import { useEffect, useMemo } from "react";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { missingPorts } from "./requiredPorts";
import type { FlowDocument, FlowNodePosition } from "./types";

interface FlowCanvasProps {
  document: FlowDocument;
  positions: Record<string, FlowNodePosition>;
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  /** The node a running test call (Simulator.tsx) is currently sitting on. */
  activeNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
  onSelectEdge?: (edgeId: string | null) => void;
  onNodeDragStop?: (nodeId: string, position: FlowNodePosition) => void;
  onConnect?: (source: string, target: string) => void;
}

// Matches the default `.react-flow__node-default` width (150px, from
// @xyflow/react's own stylesheet) and a fixed height for our two-line
// id/type label. Declared explicitly on every node — not left to
// auto-measurement — because MiniMap's own node filter
// (`nodeHasDimensions`) checks `node.width`/`node.height` as a fallback
// when a controlled node's `measured` value isn't populated the way it
// reads it; without this, MiniMap silently renders zero node rects (found
// by inspecting its own rendered SVG directly, not guessed at from CSS).
const NODE_WIDTH = 150;
const NODE_HEIGHT = 46;

const SELECTED_EDGE_STYLE = { stroke: "#5b9dd9", strokeWidth: 2.5 };

/**
 * Refits the viewport whenever `refitKey` changes (the document's own id
 * combined with its current node count — either a full document swap or
 * an add/remove), not on every render: a plain `fitView` boolean prop on
 * `<ReactFlow>` only fits once, on mount. Found the hard way, with
 * Playwright driving a real connect attempt (the same investigative
 * discipline Slice Y used for its two real @xyflow/react bugs): a node
 * added after mount kept whatever pan/zoom was already in place, so a
 * node placed to the right of the current layout (nodeGraph.ts's own
 * `addNode` heuristic) rendered entirely outside the canvas's own
 * clipped, visible area — present in the DOM, invisible and unreachable
 * on screen, exactly the "why didn't my connection register" symptom
 * that surfaced this. Deliberately NOT keyed on the full node list:
 * dragging or editing a node's config must not yank the user's own
 * pan/zoom back to fit-everything on every keystroke.
 *
 * `<ReactFlow>`'s own default `minZoom` (0.5) turned out to be the actual
 * cause, not an effect-timing race: once a layout needs to shrink past
 * 0.5x to fit every node in the canvas's own (intentionally narrow)
 * width, fitView cannot zoom out further and instead leaves nodes past
 * the edge — confirmed directly by reading the rendered viewport's own
 * `scale(...)` before and after adding a node, which never moved below
 * 0.5 until `minZoom` was lowered below on `<ReactFlow>` itself.
 */
function FitViewOnNodeCountChange({ refitKey }: { refitKey: string }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    fitView({ duration: 200 });
    // fitView's own identity is stable across renders (from useReactFlow),
    // so refitKey is the only real dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refitKey]);
  return null;
}

/**
 * Renders a flow document as a graph: click-to-select (a node or an
 * edge), drag-to-reposition, drag-between-handles-to-connect.
 *
 * Positioning is the caller's concern (FlowPreview computes it via
 * layoutFlow and owns what a drag commits back to the document) — this
 * component only renders whatever `positions` it is given and reports
 * clicks/drags/connects upward. Add/remove nodes live in Palette.tsx/
 * Inspector.tsx; connecting is a single generic handle per node, not
 * port-aware — every `type: "default"` node already renders one target
 * (top) and one source (bottom) handle, and `onConnect` reports the new
 * edge with no port name of its own; the caller (FlowPreview) is the one
 * that decides the new edge's default port and lets it be edited
 * afterward. This is deliberately not port-specific: connecting still
 * cannot offer a "connect only to a valid port" guarantee, and a wrong
 * port name is still only caught after the fact, via the diagnostics
 * panel.
 *
 * What it CAN show now (Slice AM): every node whose own required ports
 * (requiredPorts.ts, mirroring Go's real per-node-type port table) are
 * not all wired to an outgoing edge gets a `flow-node--missing-port`
 * class — a visual "this node has nowhere for its timeout/error/etc. to
 * go" marker, computed fresh from `document` on every render so it stays
 * correct through adds, removes, reconnects and config edits (a gather
 * node's own port list depends on its `valid_keys`, so editing that
 * config can add or remove a missing-port marker with no edge change at
 * all).
 *
 * `activeNodeId` (Slice AO) is a completely separate highlight from
 * `selectedNodeId` — a running test call (Simulator.tsx, simulate.ts) marks
 * whatever node it is currently sitting on with `flow-node--active`, and
 * this never interacts with node/edge selection or editing at all; a
 * tester can watch the highlighted node move while still being free to
 * click around and edit something else.
 *
 * Wrapped in its own `ReactFlowProvider`: `FitViewOnNodeCountChange` needs
 * `useReactFlow()`, which only works inside one.
 */
export function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function FlowCanvasInner({
  document,
  positions,
  selectedNodeId,
  selectedEdgeId,
  activeNodeId,
  onSelectNode,
  onSelectEdge,
  onNodeDragStop,
  onConnect,
}: FlowCanvasProps) {
  const nodes: Node[] = useMemo(
    () =>
      document.nodes.map((node) => {
        const hasMissingPort = missingPorts(document, node.id).length > 0;
        const classes = [node.id === document.start_node_id ? "flow-node--start" : ""];
        if (node.id === selectedNodeId) classes.push("flow-node--selected");
        if (hasMissingPort) classes.push("flow-node--missing-port");
        if (node.id === activeNodeId) classes.push("flow-node--active");
        return {
          id: node.id,
          position: positions[node.id] ?? { x: 0, y: 0 },
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          style: { width: NODE_WIDTH, height: NODE_HEIGHT },
          data: {
            label: (
              <div className="flow-node">
                <div className="flow-node__id">
                  {node.id}
                  {hasMissingPort ? (
                    <span className="flow-node__warning" title="Missing a required outgoing port" aria-hidden="true">
                      {" "}
                      ⚠
                    </span>
                  ) : null}
                </div>
                <div className="flow-node__type">{node.type}</div>
              </div>
            ),
          },
          className: classes.filter(Boolean).join(" "),
          draggable: true,
          connectable: true,
        };
      }),
    [document, positions, selectedNodeId, activeNodeId],
  );

  const edges: Edge[] = useMemo(
    () =>
      document.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.port,
        ...(edge.id === selectedEdgeId ? { style: SELECTED_EDGE_STYLE } : {}),
      })),
    [document, selectedEdgeId],
  );

  function handleConnect(connection: Connection) {
    if (connection.source && connection.target) {
      onConnect?.(connection.source, connection.target);
    }
  }

  function clearSelection() {
    onSelectNode?.(null);
    onSelectEdge?.(null);
  }

  return (
    <div className="flow-canvas" aria-label={`Flow graph for ${document.id}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodesDraggable
        nodesConnectable
        elementsSelectable
        minZoom={0.05}
        colorMode="dark"
        proOptions={{ hideAttribution: true }}
        /* Each handler reports only its own kind of selection — the
           caller's onSelectNode/onSelectEdge already clear the other side
           themselves (FlowPreview's handleSelectNode/handleSelectEdge).
           Calling both callbacks here too double-clears: a node click
           would set selectedNodeId, then immediately reset it back to
           null via this component's own onSelectEdge?.(null) call
           triggering the caller's "also clear selectedNodeId" side
           effect — the two layers fighting over the same state, found
           with Playwright when a plain click never selected anything. */
        onNodeClick={(_, node) => onSelectNode?.(node.id)}
        onEdgeClick={(_, edge) => onSelectEdge?.(edge.id)}
        onPaneClick={clearSelection}
        onNodeDragStop={(_, node) => onNodeDragStop?.(node.id, node.position)}
        onConnect={handleConnect}
      >
        <FitViewOnNodeCountChange refitKey={`${document.id}:${nodes.length}`} />
        <Background />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          bgColor="#171a21"
          maskColor="rgba(15, 17, 22, 0.65)"
          nodeColor="#5b9dd9"
          nodeStrokeColor="#5b9dd9"
          nodeStrokeWidth={2}
        />
      </ReactFlow>
    </div>
  );
}
